import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePaiement, usePanier, useFormatage } from '../hooks/useAgroshopHooks';
import { authService, commandesService, clientsService, produitsService, wishlistService, autoReorderService } from '../services/api';
import { getErrorMessage } from '../utils/errorFormatter';
import { handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageFallback';

const OPERATEURS = [
  { value: 'orange_money', label: 'Orange Money' },
  { value: 'wave', label: 'Wave' },
  { value: 'moov_money', label: 'Moov Money' },
  { value: 'especes', label: 'Paiement à la livraison (Espèces)' },
];

const SUPPORT_WHATSAPP = '22374146816';

export default function CartPage({ currentUser }) {
  const { items, ajouter, modifier, supprimer, sousTotal, vider } = usePanier(!!currentUser);
  const { formatPrix } = useFormatage();
  const paiement = usePaiement();
  const [autoReorderEnabled, setAutoReorderEnabled] = useState(false);
  const [autoReorderFrequency, setAutoReorderFrequency] = useState('monthly');
  const [recommendations, setRecommendations] = useState([]);
  const [suggestionError, setSuggestionError] = useState('');
  const [savedItems, setSavedItems] = useState(wishlistService.getItems());
  const [orderData, setOrderData] = useState({
    mode_retrait: 'livraison',
    region_livraison: '',
    instructions_livraison: '',
    date_livraison_souhaitee: '',
    notes_client: '',
    adresse_id: null,
  });
  const [expressDelivery, setExpressDelivery] = useState(false);
  const [deliverySlot, setDeliverySlot] = useState('');
  const [commande, setCommande] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [operateur, setOperateur] = useState('orange_money');
  const [telephone, setTelephone] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [addingAddress, setAddingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    region: '',
    ville: '',
    quartier: '',
    adresse_complete: '',
  });
  const [addressError, setAddressError] = useState('');
  const [searchParams] = useSearchParams();

  const statusSteps = [
    { key: 'confirmee', label: 'Confirmée' },
    { key: 'payee', label: 'Payée' },
    { key: 'preparation', label: 'En préparation' },
    { key: 'expedition', label: 'Expédiée' },
    { key: 'livree', label: 'Livrée' },
  ];

  const connected = !!currentUser;

  const totalItems = items.reduce((total, item) => total + item.quantite, 0);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);

  const totalAfterDiscount = Math.max(0, sousTotal - discountAmount);
  const couponDefinitions = {
    AGRO10: { percent: 0.1, label: '10% de réduction' },
    PROMO5: { percent: 0.05, label: '5% de réduction' },
    PACK15: { percent: 0.15, label: '15% de réduction pour commandes pro' },
  };

  const getItemUnitPrice = (produit) => Number(produit.prix_promotion || produit.prix_reduit || produit.promotion_prix || produit.prix_unitaire || produit.price || 0);

  useEffect(() => {
    if (!couponApplied) return;
    setDiscountAmount(Math.round(sousTotal * couponApplied.percent));
  }, [sousTotal, couponApplied]);

  const handleApplyCoupon = () => {
    const code = (couponCode || '').trim().toUpperCase();
    if (!code) {
      setCouponError('Entrez un code promo valide.');
      return;
    }
    const coupon = couponDefinitions[code];
    if (!coupon) {
      setCouponError('Code promo invalide. Essayez AGRO10, PROMO5 ou PACK15.');
      return;
    }
    if (sousTotal <= 0) {
      setCouponError('Votre panier est vide ou le montant est trop faible pour appliquer ce code.');
      return;
    }
    setCouponApplied({ code, ...coupon });
    setCouponError('');
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setDiscountAmount(0);
    setCouponCode('');
    setCouponError('');
  };

  const checkoutHighlights = [
    { icon: '🔒', title: 'Paiement sécurisé', description: 'Transactions protégées par HTTPS/SSL et paiement mobile sécurisé.' },
    { icon: '🚚', title: 'Livraison rapide', description: 'Livraison nationale sous 2-3 jours ou retrait en boutique sous 24h.' },
    { icon: '🛡️', title: 'Garantie 7 jours', description: 'Retour possible sous 7 jours pour produit non conforme.' },
  ];

  const paymentMethodsLabel = OPERATEURS.map((method) => method.label).join(' • ');

  const modeRetraitLabel = {
    livraison: 'Livraison à domicile',
    retrait: 'Retrait en boutique',
    point_relais: 'Point relais',
  }[orderData.mode_retrait] || 'Livraison à domicile';

  const handleChange = (field) => (event) => {
    setOrderData({ ...orderData, [field]: event.target.value });
  };

  useEffect(() => {
    if (!currentUser?.client?.id) return;

    const loadAddresses = async () => {
      try {
        const saved = await clientsService.getAdresses(currentUser.client.id);
        setAddresses(Array.isArray(saved) ? saved : saved.results || []);
        if (Array.isArray(saved) && saved.length > 0) {
          setSelectedAddressId(saved[0].id);
          setOrderData((prev) => ({
            ...prev,
            adresse_id: saved[0].id,
            region_livraison: saved[0].region || prev.region_livraison,
          }));
        }
      } catch (e) {
        setAddressError(getErrorMessage(e));
      }
    };

    loadAddresses();
  }, [currentUser]);

  useEffect(() => {
    let mounted = true;

    const loadRecommendations = async () => {
      if (!items.length) {
        setRecommendations([]);
        setSuggestionError('');
        return;
      }

      try {
        const ids = [...new Set(items.map((item) => item.produit.id))].slice(0, 3);
        const requests = ids.flatMap((id) => [
          produitsService.getComplementaires(id).catch(() => []),
          produitsService.getAchetesEnsemble(id).catch(() => []),
        ]);

        const results = await Promise.all(requests);
        const suggestions = results.flatMap((result) => Array.isArray(result) ? result : result.results || []);

        const filtered = suggestions.filter((produit) => !items.some((item) => item.produit.id === produit.id));
        const unique = [];
        const seenIds = new Set();

        for (const produit of filtered) {
          if (!seenIds.has(produit.id) && unique.length < 8) {
            seenIds.add(produit.id);
            unique.push(produit);
          }
        }

        if (mounted) {
          setRecommendations(unique);
          setSuggestionError('');
        }
      } catch (e) {
        if (mounted) {
          setSuggestionError(getErrorMessage(e));
        }
      }
    };

    loadRecommendations();

    return () => {
      mounted = false;
    };
  }, [items]);

  useEffect(() => {
    const handleWishlistUpdate = () => setSavedItems(wishlistService.getItems());
    window.addEventListener('wishlist-mis-a-jour', handleWishlistUpdate);
    return () => window.removeEventListener('wishlist-mis-a-jour', handleWishlistUpdate);
  }, []);

  const handleSaveForLater = (produitId) => {
    const item = items.find((entry) => entry.produit.id === produitId);
    if (!item) return;
    wishlistService.add(item.produit);
    supprimer(produitId);
    setSavedItems(wishlistService.getItems());
  };

  const handleMoveToCart = (produit) => {
    ajouter(produit, 1);
    wishlistService.remove(produit.id);
    setSavedItems(wishlistService.getItems());
  };

  const handleRemoveSavedItem = (produitId) => {
    wishlistService.remove(produitId);
    setSavedItems(wishlistService.getItems());
  };

  const bulkItems = items.filter((item) => item.produit.prix_gros && item.produit.quantite_min_gros);
  const bulkQuoteEnabled = bulkItems.length > 0;

  const handleRequestQuote = () => {
    const subject = 'Demande de devis B2B AgroShop';
    const body = [
      'Bonjour,',
      '',
      'Je souhaite recevoir un devis professionnel pour la commande suivante :',
      '',
      ...items.map((item) => `- ${item.quantite} x ${item.produit.nom} (${item.produit.reference || item.produit.id}) — ${formatPrix(item.produit.prix_unitaire)} / unité`),
      '',
      'Merci de proposer une offre professionnelle avec livraison et conditions de paiement.',
    ].filter(Boolean).join('\n');
    window.location.href = `mailto:it43694@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openWhatsAppCheckout = () => {
    if (!items.length) return;

    const text = [
      'Bonjour AgroShop,',
      '',
      'Je souhaite commander les produits suivants via WhatsApp :',
      '',
      ...items.map((item) => `- ${item.quantite} x ${item.produit.nom} (${item.produit.reference || item.produit.id}) — ${formatPrix(item.produit.prix_unitaire)}`),
      '',
      `Total estimé : ${totalAfterDiscount.toLocaleString()} FCFA`,
      orderData.instructions_livraison ? `Instructions : ${orderData.instructions_livraison}` : '',
      orderData.date_livraison_souhaitee ? `Livraison souhaitée : ${orderData.date_livraison_souhaitee}` : '',
      '',
      'Merci de me confirmer la commande et les modalités de paiement.',
    ].filter(Boolean).join('\n');

    window.open(
      `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(text)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  useEffect(() => {
    const commandeId = searchParams.get('commande');
    if (!commandeId) return;

    const loadCommande = async () => {
      try {
        setLoading(true);
        const existingCommande = await commandesService.getDetail(commandeId);
        setCommande(existingCommande);
      } catch (e) {
        setError(getErrorMessage(e));
      } finally {
        setLoading(false);
      }
    };

    loadCommande();
  }, [searchParams]);

  const handleAddressSelect = (event) => {
    const selectedId = event.target.value;
    setSelectedAddressId(selectedId);
    const selected = addresses.find((addr) => String(addr.id) === String(selectedId));
    if (selected) {
      setOrderData((prev) => ({
        ...prev,
        adresse_id: selected.id,
        region_livraison: selected.region || prev.region_livraison,
      }));
    } else {
      setOrderData((prev) => ({ ...prev, adresse_id: null }));
    }
  };

  const handleNewAddressChange = (field) => (event) => {
    setNewAddress({ ...newAddress, [field]: event.target.value });
  };

  const handleAddAddress = async (event) => {
    event.preventDefault();
    setAddressError('');
    if (!currentUser?.client?.id) {
      setAddressError('Impossible de sauvegarder l’adresse sans compte client.');
      return;
    }
    if (!newAddress.region || !newAddress.ville || !newAddress.adresse_complete) {
      setAddressError('Veuillez remplir la région, la ville et l’adresse complète.');
      return;
    }
    try {
      const created = await clientsService.ajouterAdresse(currentUser.client.id, newAddress);
      const nextAddresses = [...addresses, created];
      setAddresses(nextAddresses);
      setSelectedAddressId(created.id);
      setOrderData((prev) => ({
        ...prev,
        adresse_id: created.id,
        region_livraison: created.region || prev.region_livraison,
      }));
      setAddingAddress(false);
      setNewAddress({ region: '', ville: '', quartier: '', adresse_complete: '' });
    } catch (e) {
      setAddressError(getErrorMessage(e));
    }
  };

  const handleSubmitCommande = async (event) => {
    event.preventDefault();
    if (!connected) {
      setError('Connectez-vous pour confirmer votre commande.');
      return;
    }
    if (!items.length) {
      setError('Votre panier est vide. Ajoutez des produits avant de confirmer.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const livraisonInstructions = [
        orderData.instructions_livraison,
        expressDelivery ? 'Livraison express demandée' : '',
        deliverySlot ? `Créneau souhaité: ${deliverySlot}` : '',
      ]
        .filter(Boolean)
        .join(' | ');

      const payload = {
        ...orderData,
        adresse_id: orderData.adresse_id || null,
        date_livraison_souhaitee: orderData.date_livraison_souhaitee || null,
        notes_client: [orderData.notes_client, livraisonInstructions].filter(Boolean).join(' | '),
        ...(couponApplied ? { code_promo: couponApplied.code } : {}),
      };
      const data = await commandesService.creer(payload);
      setCommande(data);
      // Si l'utilisateur a activé l'auto-reorder, enregistrer localement
      try {
        if (autoReorderEnabled && data && data.id) {
          autoReorderService.save({
            commandeId: data.id,
            date: new Date().toISOString(),
            frequency: autoReorderFrequency,
            montant: data.montant_total,
          });
        }
      } catch (e) {
        // Ne pas bloquer l'expérience si l'enregistrement local échoue
      }
      setTelephone('');
      paiement.reinitialiser();
      setError('');
    } catch (err) {
      console.error('Erreur création commande:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCart = async () => {
    if (!connected) {
      setError('Connectez-vous pour sauvegarder votre panier sur le serveur.');
      setSuccessMessage('');
      return;
    }
    setError('');
    setSuccessMessage('');
    try {
      const livraisonInstructions = [
        orderData.instructions_livraison,
        expressDelivery ? 'Livraison express demandée' : '',
        deliverySlot ? `Créneau souhaité: ${deliverySlot}` : '',
      ]
        .filter(Boolean)
        .join(' | ');

      await commandesService.sauvegarderPanier({
        lignes: items.map((item) => ({
          produit_id: item.produit.id,
          quantite: item.quantite,
          notes: item.notes || '',
        })),
        mode_retrait: orderData.mode_retrait,
        region_livraison: orderData.region_livraison,
        adresse_id: orderData.adresse_id || null,
        instructions_livraison: livraisonInstructions || orderData.instructions_livraison,
        date_livraison_souhaitee: orderData.date_livraison_souhaitee || null,
        notes_client: orderData.notes_client,
      });
      setSuccessMessage('Panier sauvegardé.');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleInitiatePayment = async (event) => {
    event.preventDefault();
    if (!commande) return;
    setError('');
    if (!telephone) {
      setError('Veuillez saisir votre numéro de téléphone pour recevoir la demande de paiement.');
      return;
    }
    await paiement.initier(commande.id, operateur, telephone);
  };

  useEffect(() => {
    if (currentUser?.client?.mode_paiement_prefere) {
      setOperateur(currentUser.client.mode_paiement_prefere);
    }
    if (currentUser?.client?.telephone) {
      setTelephone(currentUser.client.telephone);
    }
  }, [currentUser]);

  return (
    <div className="ag-card">
      <h2>Panier</h2>
      <div style={{ marginBottom: '18px', padding: '16px', borderRadius: '16px', backgroundColor: '#ecfdf5', border: '1px solid #d1fae5', color: '#065f46' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', fontWeight: 600 }}>
          <span>🔒</span>
          <span>Connexion sécurisée et protection des données</span>
        </div>
        <div style={{ display: 'grid', gap: '6px', fontSize: '0.95rem' }}>
          <div>Vos informations sont chiffrées via HTTPS/SSL et traitées conformément à notre politique de confidentialité.</div>
          <div>Retour possible sous 7 jours en cas de produit non conforme.</div>
          <div>Voir notre <Link to="/privacy">politique de confidentialité</Link>.</div>
        </div>
      </div>
      {!connected && (
        <div className="ag-alert">
          Vous pouvez ajouter et modifier votre panier localement. Connectez-vous pour enregistrer le panier sur le serveur et confirmer votre commande.
        </div>
      )}
      {items.length === 0 ? (
        <div className="ag-alert">Votre panier est vide. Retournez à l'accueil pour ajouter des produits.</div>
      ) : (
        <div className="ag-cart-list">
          {items.map((item) => {
            const itemUnitPrice = getItemUnitPrice(item.produit);
            const itemOriginalPrice = Number(item.produit.prix_unitaire || item.produit.price || 0);
            const itemTotal = item.quantite * itemUnitPrice;
            const hasPromotion = itemUnitPrice > 0 && itemOriginalPrice > itemUnitPrice;
            return (
              <div key={item.produit.id} className="ag-cart-item">
                <div>
                  <strong>{item.produit.nom || item.produit.name || 'Produit'}</strong>
                  <div className="ag-muted">{item.produit.categorie?.nom || item.produit.categorie?.name || ''}</div>
                  <div className="ag-cart-item-pricing" style={{ marginTop: '6px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      {hasPromotion ? (
                        <span>
                          <strong>{formatPrix(itemUnitPrice)}</strong>
                          <span className="ag-price-original" style={{ marginLeft: '8px' }}>{formatPrix(itemOriginalPrice)}</span>
                        </span>
                      ) : (
                        <strong>{formatPrix(itemUnitPrice)}</strong>
                      )}
                    </div>
                    <div className="ag-muted" style={{ fontSize: '0.95rem' }}>
                      Total {formatPrix(itemTotal)} • {item.quantite} unité{item.quantite > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div className="ag-cart-actions">
                  <input
                    type="number"
                    min="1"
                    value={item.quantite}
                    onChange={(e) => modifier(item.produit.id, Number(e.target.value) || 1)}
                  />
                  <button type="button" className="ag-btn-secondary" onClick={() => supprimer(item.produit.id)}>
                    Supprimer
                  </button>
                  <button type="button" className="ag-btn" onClick={() => handleSaveForLater(item.produit.id)}>
                    Enregistrer pour plus tard
                  </button>
                </div>
                {item.produit.prix_gros && item.produit.quantite_min_gros && (
                  <div className="ag-note" style={{ marginTop: '8px' }}>
                    {item.quantite >= item.produit.quantite_min_gros ? (
                      <span>Prix gros appliqué : <strong>{formatPrix(item.produit.prix_gros)} / unité</strong> pour {item.quantite} unités ou plus.</span>
                    ) : (
                      <span>Tarif B2B disponible à partir de <strong>{item.produit.quantite_min_gros} unités</strong> : <strong>{formatPrix(item.produit.prix_gros)} / unité</strong>.</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {savedItems.length > 0 && (
        <section className="ag-card" style={{ marginTop: '20px', padding: '18px' }}>
          <h3>Produits enregistrés pour plus tard</h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            {savedItems.map((saved) => (
              <div key={saved.id} className="ag-cart-item" style={{ alignItems: 'center' }}>
                <div>
                  <strong>{saved.nom}</strong>
                  <div className="ag-muted">{saved.reference || ''}</div>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button type="button" className="ag-btn" onClick={() => handleMoveToCart(saved)}>
                    Remettre au panier
                  </button>
                  <button type="button" className="ag-btn-secondary" onClick={() => handleRemoveSavedItem(saved.id)}>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {items.length > 0 && (
        <section className="ag-card" style={{ marginTop: '20px', padding: '18px', border: '1px dashed #9ca3af', backgroundColor: '#fafbfc' }}>
          <h3>Code promo</h3>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Entrez AGRO10, PROMO5..."
              style={{ flex: '1 1 240px', minWidth: '160px' }}
            />
            <button type="button" className="ag-btn" onClick={handleApplyCoupon}>
              Appliquer
            </button>
            {couponApplied && (
              <button type="button" className="ag-btn ag-btn-secondary" onClick={handleRemoveCoupon}>
                Supprimer
              </button>
            )}
          </div>
          {couponError && <div className="ag-alert ag-alert-error" style={{ marginTop: '12px' }}>{couponError}</div>}
          {couponApplied && (
            <div className="ag-alert ag-alert-success" style={{ marginTop: '12px' }}>
              Code <strong>{couponApplied.code}</strong> appliqué : {couponApplied.label}
            </div>
          )}
        </section>
      )}

      <div className="ag-order-summary">
        <div>Articles: {totalItems}</div>
        <div>Sous-total: {sousTotal.toLocaleString()} FCFA</div>
        {couponApplied && <div>Remise ({couponApplied.code}) : -{discountAmount.toLocaleString()} FCFA</div>}
        {couponApplied && <div><strong>Total : {totalAfterDiscount.toLocaleString()} FCFA</strong></div>}
      </div>

      <section className="ag-card" style={{ marginTop: '20px', padding: '18px', backgroundColor: '#f4f7fb', border: '1px solid #dbe3ee' }}>
        <div style={{ display: 'grid', gap: '14px' }}>
          <h3>Pourquoi commander avec AgroShop</h3>
          <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {checkoutHighlights.map((highlight) => (
              <div key={highlight.title} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ fontSize: '1.35rem' }}>{highlight.icon}</div>
                <div>
                  <strong>{highlight.title}</strong>
                  <div style={{ color: '#475569', marginTop: '4px', fontSize: '0.95rem' }}>{highlight.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {bulkQuoteEnabled && (
        <section className="ag-card" style={{ marginTop: '20px', padding: '18px', backgroundColor: '#f8fafc', border: '1px solid #cbd5e1' }}>
          <h3>Tarif professionnel disponible</h3>
          <p style={{ margin: '8px 0' }}>
            Certains produits de votre panier sont éligibles à un prix B2B. Demandez un devis personnalisé pour obtenir un tarif en gros, une livraison prioritaire et des conditions de paiement adaptées.
          </p>
          <button type="button" className="ag-btn ag-btn-secondary" onClick={handleRequestQuote}>
            Demander un devis B2B
          </button>
        </section>
      )}

      {items.length > 0 && (
        <form className="ag-form" onSubmit={handleSubmitCommande}>
          <h3>Confirmer la commande</h3>

          <section className="ag-card" style={{ marginBottom: '18px', padding: '16px', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe' }}>
            <h4 style={{ margin: '0 0 10px' }}>Résumé de la commande</h4>
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Articles</span>
                <strong>{totalItems}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Sous-total</span>
                <strong>{sousTotal.toLocaleString()} FCFA</strong>
              </div>
              {couponApplied && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Réduction promo ({couponApplied.code})</span>
                  <strong>-{discountAmount.toLocaleString()} FCFA</strong>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total estimé</span>
                <strong>{totalAfterDiscount.toLocaleString()} FCFA</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Mode de retrait</span>
                <strong>{modeRetraitLabel}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Frais estimés</span>
                <strong>{'Calculés à la confirmation'}</strong>
              </div>
            </div>
          </section>

          <label>
            Mode de retrait
            <select value={orderData.mode_retrait} onChange={handleChange('mode_retrait')}>
              <option value="livraison">Livraison à domicile</option>
              <option value="retrait">Retrait en boutique</option>
              <option value="point_relais">Point relais</option>
            </select>
          </label>

          {addresses.length > 0 && (
            <label>
              Adresse de livraison enregistrée
              <select value={selectedAddressId} onChange={handleAddressSelect}>
                <option value="">Nouvelle adresse</option>
                {addresses.map((adresse) => (
                  <option key={adresse.id} value={adresse.id}>
                    {adresse.region} — {adresse.ville} — {adresse.adresse_complete}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label>
            Région de livraison
            <input
              type="text"
              value={orderData.region_livraison}
              onChange={handleChange('region_livraison')}
              placeholder="Ex: Bamako"
            />
          </label>

          <label>
            Créneau de livraison souhaité
            <select
              value={deliverySlot}
              onChange={(event) => setDeliverySlot(event.target.value)}
            >
              <option value="">Aucun</option>
              <option value="8h-12h">8h-12h</option>
              <option value="12h-16h">12h-16h</option>
              <option value="16h-19h">16h-19h</option>
            </select>
          </label>

          <div className="ag-address-section">
            <div className="ag-address-actions">
              <button
                type="button"
                className="ag-btn ag-btn-small"
                onClick={() => setAddingAddress((prev) => !prev)}
              >
                {addingAddress ? 'Masquer le formulaire' : 'Ajouter une adresse'}
              </button>
            </div>
            {addingAddress && (
              <div className="ag-card ag-address-card">
                <label>
                  Région
                  <input
                    type="text"
                    value={newAddress.region}
                    onChange={handleNewAddressChange('region')}
                    placeholder="Ex: Bamako"
                    required
                  />
                </label>
                <label>
                  Ville
                  <input
                    type="text"
                    value={newAddress.ville}
                    onChange={handleNewAddressChange('ville')}
                    placeholder="Ex: Commune II"
                    required
                  />
                </label>
                <label>
                  Quartier
                  <input
                    type="text"
                    value={newAddress.quartier}
                    onChange={handleNewAddressChange('quartier')}
                    placeholder="Ex: Hippodrome"
                  />
                </label>
                <label>
                  Adresse complète
                  <textarea
                    rows="2"
                    value={newAddress.adresse_complete}
                    onChange={handleNewAddressChange('adresse_complete')}
                    placeholder="Ex: Maison 123, rue de l'Office"
                    required
                  />
                </label>
                {addressError && <div className="ag-alert ag-alert-error">{addressError}</div>}
                <button type="button" className="ag-btn" onClick={handleAddAddress}>
                  Enregistrer l’adresse
                </button>
              </div>
            )}
          </div>

          <label>
            Instructions spéciales
            <textarea
              rows="3"
              value={orderData.instructions_livraison}
              onChange={handleChange('instructions_livraison')}
              placeholder="Ex: Appeler à l'arrivée"
            />
          </label>

          <label>
            Date souhaitée de livraison
            <input
              type="date"
              value={orderData.date_livraison_souhaitee}
              onChange={handleChange('date_livraison_souhaitee')}
            />
          </label>

          <label>
            Notes pour le vendeur
            <textarea
              rows="3"
              value={orderData.notes_client}
              onChange={handleChange('notes_client')}
              placeholder="Ex: Livraison après 16h"
            />
          </label>

          {error && <div className="ag-alert ag-alert-error">{error}</div>}
          {successMessage && <div className="ag-alert ag-alert-success">{successMessage}</div>}

          <div className="ag-form-actions">
            <button className="ag-btn" type="submit" disabled={loading || !connected}>
              {loading ? 'Création en cours…' : 'Payer et commander'}
            </button>
            <button type="button" className="ag-btn ag-btn-secondary" onClick={openWhatsAppCheckout} disabled={!items.length}>
              Commander via WhatsApp
            </button>
            <button type="button" className="ag-btn-secondary" onClick={handleSaveCart} disabled={!connected}>
              Enregistrer le panier
            </button>
            <button type="button" className="ag-btn-secondary" onClick={vider}>
              Vider le panier
            </button>
          </div>

          <div style={{ marginTop: '14px', fontSize: '0.95rem', color: '#475569' }}>
            Méthodes de paiement acceptées : <strong>{paymentMethodsLabel}</strong>
          </div>
        </form>
      )}

      {recommendations.length > 0 && (
        <section className="ag-card ag-recommendation-card">
          <div className="ag-recommendation-header">
            <h3>Vous avez oublié :</h3>
            <p>Suggestions de produits complémentaires et bundles souvent achetés ensemble.</p>
          </div>
          {suggestionError && <div className="ag-alert ag-alert-error">{suggestionError}</div>}
          <div className="ag-products">
            {recommendations.map((produit) => (
              <article key={produit.id} className="ag-product-card">
                <div className="ag-product-media" role="img" aria-label={produit.nom || produit.name || ''}>
                  {produit.photo_principale ? (
                    <img
                      src={produit.photo_principale}
                      loading="lazy"
                      alt={produit.nom || produit.name || ''}
                      onError={(e) => { handleImageError(e, produit); }}
                    />
                  ) : (
                    <div className="ag-product-media-icon">🛒</div>
                  )}
                </div>
                <div className="ag-product-body">
                  <h4>{produit.nom || produit.name || 'Produit recommandé'}</h4>
                  <div className="ag-price">{produit.prix_unitaire ? `${parseInt(produit.prix_unitaire).toLocaleString()} FCFA` : '—'}</div>
                </div>
                <div className="ag-product-footer">
                  <button
                    type="button"
                    className="ag-btn ag-product-add"
                    onClick={() => ajouter(produit, 1)}
                  >
                    Ajouter au panier
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {commande && (
        <section className="ag-card ag-payment-card">
          <h3>Commande confirmée</h3>
          <p>Numéro: <strong>{commande.numero}</strong></p>
          <p>Montant total: <strong>{Number(commande.montant_total).toLocaleString()} FCFA</strong></p>
          <p>Statut: <strong>{commande.statut_display || commande.statut}</strong></p>
          <p>Livraison estimée : <strong>{commande.estimated_delivery || 'À déterminer'}</strong></p>
          <p>
            <a href={`${window.location.origin}/cmd/${commande.numero}`} target="_blank" rel="noopener noreferrer">Suivre la commande</a>
          </p>
          <div className="ag-order-tracker" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '16px 0' }}>
            {statusSteps.map((step) => {
              const active = statusSteps.findIndex((item) => item.key === commande.statut) >= statusSteps.findIndex((item) => item.key === step.key);
              return (
                <div key={step.key} className={`ag-step ${active ? 'ag-step--active' : ''}`} style={{
                  padding: '10px 14px',
                  borderRadius: '999px',
                  backgroundColor: active ? '#286ef8' : '#f0f3fb',
                  color: active ? '#fff' : '#4a5568',
                  fontSize: '0.9rem',
                }}>
                  {step.label}
                </div>
              );
            })}
          </div>

          {commande.statut === 'confirmee' ? (
            <>
              <form className="ag-form" onSubmit={handleInitiatePayment}>
                <h4>Initier le paiement</h4>
                <label>
                  Opérateur
                  <select value={operateur} onChange={(e) => setOperateur(e.target.value)}>
                    {OPERATEURS.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </label>

                <label>
                  Téléphone payeur
                  <input
                    type="text"
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                    placeholder="76000000"
                  />
                </label>

                <button className="ag-btn" type="submit" disabled={paiement.loading}>
                  {paiement.loading ? 'Initialisation...' : 'Initier le paiement'}
                </button>
                <button
                  type="button"
                  className="ag-btn ag-btn-tertiary"
                  style={{ marginLeft: '8px' }}
                  onClick={() => {
                    const subject = 'Demande de crédit / BNPL - AgroShop';
                    const body = [
                      'Bonjour AgroShop,',
                      '',
                      'Je souhaite obtenir une option de paiement différé (crédit / BNPL) pour la commande suivante :',
                      `- Commande: ${commande.numero || ''}`,
                      `- Montant: ${commande.montant_total || ''} FCFA`,
                      '',
                      'Merci de me contacter pour les conditions et le processus.',
                    ].join('\n');
                    window.location.href = `mailto:it43694@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  }}
                >
                  Demander crédit (BNPL)
                </button>
              </form>

              {paiement.erreur && <div className="ag-alert ag-alert-error">{paiement.erreur}</div>}
              {paiement.etape === 'attente' && (
                <div className="ag-alert">Paiement en attente. Statut actuel : {paiement.statutPaiement || 'En cours d’attente'}</div>
              )}
              {paiement.etape === 'confirme' && (
                <div className="ag-alert">Paiement confirmé ! Merci. Votre commande est en cours de traitement.</div>
              )}
              {paiement.etape === 'echec' && (
                <div className="ag-alert ag-alert-error">Le paiement a échoué. Réessayez ou changez d'opérateur.</div>
              )}
            </>
          ) : (
            <div className="ag-alert ag-alert-info">
              Cette commande ne peut être payée que si elle est en statut « Confirmée (en attente paiement) ».
            </div>
          )}
        </section>
      )}
    </div>
  );
}
